import IResult, { ResultType } from "../../../shared/models/result/IResult";
import { sendGet, sendPut } from "../../../shared/api/controllers/generic";
import { createOnboardingApiClient } from "./onboardingApiClient";
import { isFieldVisible } from "../../../shared/utils/sectionUtils";

export interface ApplicationModuleFieldOption {
    id?: string;
    templateFormFieldId?: string;
    dynamicFormFieldId?: string;
    label?: string;
    value?: string;
    priority?: number;
}

export interface ApplicationModuleFieldConditionalDefaultCondition {
    fieldAssociatedVariable?: string;
    operator?: string;
    value?: string;
    defaultValue?: string;
}

export interface ApplicationModuleFieldConditionalDefaults {
    conditions?: ApplicationModuleFieldConditionalDefaultCondition[];
    otherwise?: string;
}

export interface ApplicationModuleField {
    currentValue?: string;
    value?: string;
    sectionTitle?: string;
    sectionId?: string;
    appliedTemplateFormId?: string;
    id?: string;
    templateFormFieldId?: string;
    templateFormSectionId?: string;
    label?: string;
    fieldType?: string;
    placeholder?: string;
    defaultValue?: string;
    tip?: string;
    isRequired?: boolean;
    instructionsMarkdown?: string;
    title?: string;
    priority?: number;
    fileFormats?: string;
    suggestions?: string;
    conditionalDefaults?: ApplicationModuleFieldConditionalDefaults;
    associatedVariable?: string;
    options?: ApplicationModuleFieldOption[];
}

export interface AppliedModuleFieldsModel {
    appliedTemplateFormId?: string;
    templateName?: string;
    appliedTemplateName?: string;
    isCompleted?: boolean;
    lastSyncedDate?: string;
    templateFormFieldValues?: ApplicationModuleField[];
    templateFields?: ApplicationModuleField[];
    fields?: ApplicationModuleField[];
}

export interface ApplicationModuleFieldsResponse {
    applicationId?: string;
    appliedModules?: AppliedModuleFieldsModel[];
}

export interface ApplicationModuleFieldUpdate {
    associatedVariable: string;
    value: string;
    publishToProduction: boolean;
}

export interface ApplicationModuleFieldLookup {
    [associatedVariable: string]: ApplicationModuleField;
}

export interface FlattenedApplicationModuleField extends ApplicationModuleField {
    moduleTemplateName?: string;
    moduleAppliedTemplateName?: string;
}

export interface ModuleFieldUpdateResult {
    success: boolean;
    error?: string;
    resultType?: ResultType;
}

export interface LoadedApplicationModuleFieldsResult {
    result: IResult<any>;
    rawData?: any;
    parsedData?: ApplicationModuleFieldsResponse;
}

const readValue = <T = any>(source: any, camelKey: string, pascalKey?: string): T | undefined => {
    if (!source || typeof source !== "object") {
        return undefined;
    }

    if (source[camelKey] !== undefined) {
        return source[camelKey] as T;
    }

    if (pascalKey && source[pascalKey] !== undefined) {
        return source[pascalKey] as T;
    }

    return undefined;
};

const parseJsonString = (value: any): any => {
    if (typeof value !== "string") {
        return value;
    }

    try {
        const parsed = JSON.parse(value);
        return typeof parsed === "string" ? parseJsonString(parsed) : parsed;
    } catch (_) {
        return value;
    }
};

const normalizeModuleField = (rawField: any): ApplicationModuleField => ({
    currentValue: readValue<string>(rawField, "currentValue", "CurrentValue"),
    value: readValue<string>(rawField, "value", "Value"),
    sectionTitle: readValue<string>(rawField, "sectionTitle", "SectionTitle"),
    sectionId: readValue<string>(rawField, "sectionId", "SectionId"),
    appliedTemplateFormId: readValue<string>(rawField, "appliedTemplateFormId", "AppliedTemplateFormId"),
    id: readValue<string>(rawField, "id", "Id"),
    templateFormFieldId: readValue<string>(rawField, "templateFormFieldId", "TemplateFormFieldId"),
    templateFormSectionId: readValue<string>(rawField, "templateFormSectionId", "TemplateFormSectionId"),
    label: readValue<string>(rawField, "label", "Label"),
    fieldType: readValue<string>(rawField, "fieldType", "FieldType"),
    placeholder: readValue<string>(rawField, "placeholder", "Placeholder"),
    defaultValue: readValue<string>(rawField, "defaultValue", "DefaultValue"),
    tip: readValue<string>(rawField, "tip", "Tip"),
    isRequired: readValue<boolean>(rawField, "isRequired", "IsRequired"),
    instructionsMarkdown: readValue<string>(rawField, "instructionsMarkdown", "InstructionsMarkdown"),
    title: readValue<string>(rawField, "title", "Title"),
    priority: readValue<number>(rawField, "priority", "Priority"),
    fileFormats: readValue<string>(rawField, "fileFormats", "FileFormats"),
    suggestions: readValue<string>(rawField, "suggestions", "Suggestions"),
    conditionalDefaults: readValue<ApplicationModuleFieldConditionalDefaults>(rawField, "conditionalDefaults", "ConditionalDefaults"),
    associatedVariable: readValue<string>(rawField, "associatedVariable", "AssociatedVariable"),
    options: readValue<any[]>(rawField, "options", "Options")?.map(rawOption => ({
        id: readValue<string>(rawOption, "id", "Id"),
        templateFormFieldId: readValue<string>(rawOption, "templateFormFieldId", "TemplateFormFieldId"),
        dynamicFormFieldId: readValue<string>(rawOption, "dynamicFormFieldId", "DynamicFormFieldId"),
        label: readValue<string>(rawOption, "label", "Label"),
        value: readValue<string>(rawOption, "value", "Value"),
        priority: readValue<number>(rawOption, "priority", "Priority")
    }))
});

const getNestedTemplateFormFields = (rawModule: any): ApplicationModuleField[] => {
    const templateForm = readValue<any>(rawModule, "templateForm", "TemplateForm");
    const sections = readValue<any[]>(templateForm, "templateFormSections", "TemplateFormSections") || [];

    return sections.reduce((accumulator, rawSection) => {
        const sectionTitle = readValue<string>(rawSection, "title", "Title");
        const sectionId = readValue<string>(rawSection, "id", "Id");
        const fields = readValue<any[]>(rawSection, "templateFormFields", "TemplateFormFields") || [];

        fields.forEach(rawField => {
            const normalizedField = normalizeModuleField(rawField);

            accumulator.push({
                ...normalizedField,
                sectionTitle: normalizedField.sectionTitle || sectionTitle,
                sectionId: normalizedField.sectionId || sectionId
            });
        });

        return accumulator;
    }, [] as ApplicationModuleField[]);
};

const normalizeAppliedModule = (rawModule: any): AppliedModuleFieldsModel => {
    const templateForm = readValue<any>(rawModule, "templateForm", "TemplateForm");
    const templateConfiguration = readValue<any>(templateForm, "templateConfiguration", "TemplateConfiguration");
    const normalizedFieldValues = ((readValue<any[]>(rawModule, "templateFormFieldValues", "TemplateFormFieldValues")
        || []).map(normalizeModuleField));
    const normalizedFields = ((readValue<any[]>(rawModule, "templateFields", "TemplateFields")
        || readValue<any[]>(rawModule, "fields", "Fields")
        || []).map(normalizeModuleField));

    return {
        appliedTemplateFormId: readValue<string>(rawModule, "appliedTemplateFormId", "AppliedTemplateFormId")
            || readValue<string>(rawModule, "id", "Id"),
        templateName: readValue<string>(rawModule, "templateName", "TemplateName")
            || readValue<string>(templateConfiguration, "name", "Name"),
        appliedTemplateName: readValue<string>(rawModule, "appliedTemplateName", "AppliedTemplateName")
            || readValue<string>(templateConfiguration, "description", "Description")
            || readValue<string>(templateConfiguration, "name", "Name"),
        isCompleted: readValue<boolean>(rawModule, "isCompleted", "IsCompleted"),
        lastSyncedDate: readValue<string>(rawModule, "lastSyncedDate", "LastSyncedDate"),
        templateFormFieldValues: normalizedFieldValues,
        templateFields: normalizedFields.length > 0 ? normalizedFields : getNestedTemplateFormFields(rawModule)
    };
};

const normalizeModuleFieldsResponse = (rawResponse: any): ApplicationModuleFieldsResponse | undefined => {
    if (!rawResponse || typeof rawResponse !== "object") {
        return undefined;
    }

    const rawModules = Array.isArray(rawResponse)
        ? rawResponse
        : (readValue<any[]>(rawResponse, "appliedModules", "AppliedModules") || []);

    const firstModule = rawModules[0];

    return {
        applicationId: readValue<string>(rawResponse, "applicationId", "ApplicationId")
            || readValue<string>(firstModule, "appliedApplicationId", "AppliedApplicationId"),
        appliedModules: rawModules.map(normalizeAppliedModule)
    };
};

const tryParseModuleFieldsResponse = (value: any): ApplicationModuleFieldsResponse | undefined => {
    if (!value) {
        return undefined;
    }

    if (typeof value !== "string") {
        return normalizeModuleFieldsResponse(value) || (value as ApplicationModuleFieldsResponse);
    }

    try {
        const parsed = JSON.parse(value);
        if (typeof parsed === "string") {
            return tryParseModuleFieldsResponse(parsed);
        }

        return normalizeModuleFieldsResponse(parsed) || (parsed as ApplicationModuleFieldsResponse);
    } catch (_) {
        return undefined;
    }
};

const getRawModules = (response?: ApplicationModuleFieldsResponse | any): any[] => {
    const normalizedResponse = parseJsonString(response);

    if (!normalizedResponse) {
        return [];
    }

    if (Array.isArray(normalizedResponse)) {
        return normalizedResponse;
    }

    return readValue<any[]>(normalizedResponse, "appliedModules", "AppliedModules") || [];
};

const getTemplateConfigurationOrganizationId = (rawModule: any): string | undefined => {
    const templateForm = readValue<any>(rawModule, "templateForm", "TemplateForm");
    const templateConfiguration = readValue<any>(templateForm, "templateConfiguration", "TemplateConfiguration");
    return readValue<string>(templateConfiguration, "organizationId", "OrganizationId");
};

const getRawModuleFields = (rawModule: any): FlattenedApplicationModuleField[] => {
    const fieldValues = (readValue<any[]>(rawModule, "templateFormFieldValues", "TemplateFormFieldValues") || [])
        .map(normalizeModuleField);
    const directFields = ((readValue<any[]>(rawModule, "templateFields", "TemplateFields")
        || readValue<any[]>(rawModule, "fields", "Fields")
        || [])).map(normalizeModuleField);

    const mappedDirectFields = directFields.map(field => {
        const savedValue = fieldValues.find(value =>
            (field.id && value.templateFormFieldId === field.id) ||
            (field.associatedVariable && value.associatedVariable === field.associatedVariable)
        );

        return savedValue ? {
            ...field,
            ...savedValue,
            associatedVariable: savedValue.associatedVariable || field.associatedVariable,
            options: savedValue.options?.length ? savedValue.options : field.options
        } : field;
    });

    if (mappedDirectFields.length > 0 || fieldValues.length > 0) {
        return (mappedDirectFields.length > 0 ? mappedDirectFields : fieldValues).map(field => ({
            ...field,
            moduleTemplateName: readValue<string>(rawModule, "templateName", "TemplateName")
                || readValue<string>(readValue<any>(readValue<any>(rawModule, "templateForm", "TemplateForm"), "templateConfiguration", "TemplateConfiguration"), "name", "Name"),
            moduleAppliedTemplateName: readValue<string>(rawModule, "appliedTemplateName", "AppliedTemplateName")
                || readValue<string>(readValue<any>(readValue<any>(rawModule, "templateForm", "TemplateForm"), "templateConfiguration", "TemplateConfiguration"), "description", "Description")
        }));
    }

    const templateForm = readValue<any>(rawModule, "templateForm", "TemplateForm");
    const templateConfiguration = readValue<any>(templateForm, "templateConfiguration", "TemplateConfiguration");
    const sections = readValue<any[]>(templateForm, "templateFormSections", "TemplateFormSections") || [];

    return sections.reduce((accumulator, rawSection) => {
        const sectionTitle = readValue<string>(rawSection, "title", "Title");
        const sectionId = readValue<string>(rawSection, "id", "Id");
        const fields = readValue<any[]>(rawSection, "templateFormFields", "TemplateFormFields") || [];

        fields.forEach(rawField => {
            const normalizedField = normalizeModuleField(rawField);
            accumulator.push({
                ...normalizedField,
                sectionTitle: normalizedField.sectionTitle || sectionTitle,
                sectionId: normalizedField.sectionId || sectionId,
                moduleTemplateName: readValue<string>(rawModule, "templateName", "TemplateName")
                    || readValue<string>(templateConfiguration, "name", "Name"),
                moduleAppliedTemplateName: readValue<string>(rawModule, "appliedTemplateName", "AppliedTemplateName")
                    || readValue<string>(templateConfiguration, "description", "Description")
                    || readValue<string>(templateConfiguration, "name", "Name")
            });
        });

        return accumulator;
    }, [] as FlattenedApplicationModuleField[]);
};

const extractFlattenedApplicationModuleFields = (response?: ApplicationModuleFieldsResponse | any): FlattenedApplicationModuleField[] => {
    return getRawModules(response).reduce((accumulator, rawModule) => {
        return accumulator.concat(getRawModuleFields(rawModule));
    }, [] as FlattenedApplicationModuleField[]);
};

const getResultError = (result: { errors?: string[]; resultType?: string }, fallback: string): string => {
    if (result?.errors && result.errors.length > 0) {
        return result.errors[0];
    }

    if (result?.resultType && result.resultType !== "Ok") {
        return `${fallback} (${result.resultType})`;
    }

    return fallback;
};

export const loadApplicationModuleFields = async (
    applicationId: string,
    baseUrl: string,
    onboardingApiKey?: string
): Promise<LoadedApplicationModuleFieldsResult> => {
    const result = onboardingApiKey
        ? await createOnboardingApiClient(onboardingApiKey, baseUrl).get<any>(`/ApplicationModuleFields/${applicationId}/allfields`)
        : await sendGet<any>(`/ApplicationModuleFields/${applicationId}/allfields`, baseUrl);
    const rawData = result.data;
    let parsedData: ApplicationModuleFieldsResponse | undefined;

    if (result.resultType === "Ok") {
        parsedData = tryParseModuleFieldsResponse(rawData);
    }

    return {
        result,
        rawData,
        parsedData
    };
};

export const updateApplicationModuleField = async (
    applicationId: string,
    associatedVariable: string,
    value: string,
    baseUrl?: string,
    onboardingApiKey?: string
): Promise<ModuleFieldUpdateResult> => {
    const request = {
        associatedVariable,
        value,
        publishToProduction: false
    } as ApplicationModuleFieldUpdate;
    const result = onboardingApiKey && baseUrl
        ? await createOnboardingApiClient(onboardingApiKey, baseUrl)
            .put<any>(`/ApplicationModuleFields/${applicationId}/updatefield`, request)
        : await sendPut<any>(`/ApplicationModuleFields/${applicationId}/updatefield`, request, baseUrl);

    if (result.resultType !== "Ok") {
        return {
            success: false,
            error: getResultError(result, `We could not save ${associatedVariable}.`),
            resultType: result.resultType
        };
    }

    return {
        success: true
    };
};

export const flattenApplicationModuleFields = (response?: ApplicationModuleFieldsResponse | any): ApplicationModuleFieldLookup => {
    const lookup: ApplicationModuleFieldLookup = {};

    extractFlattenedApplicationModuleFields(response).forEach(field => {
        if (!field?.associatedVariable?.length) {
            return;
        }

        if (!lookup[field.associatedVariable]) {
            lookup[field.associatedVariable] = field;
            return;
        }

        const existingValueLength = lookup[field.associatedVariable]?.currentValue?.length || 0;
        const candidateValueLength = field.currentValue?.length || 0;

        if (candidateValueLength > existingValueLength) {
            lookup[field.associatedVariable] = field;
        }
    });

    return lookup;
};

/**
 * Returns only the fields from an organization's applied Spark configuration.
 * Those fields are the authoritative location-specific schema; a regular
 * flattened location lookup can also contain inherited shared fields.
 */
export const flattenOrganizationOwnedApplicationModuleFields = (
    response: ApplicationModuleFieldsResponse | any,
    organizationId: string
): ApplicationModuleFieldLookup => {
    const lookup: ApplicationModuleFieldLookup = {};

    getRawModules(response)
        .filter(rawModule => getTemplateConfigurationOrganizationId(rawModule) === organizationId)
        .forEach(rawModule => {
            getRawModuleFields(rawModule).forEach(field => {
                const associatedVariable = field?.associatedVariable?.trim();
                if (!associatedVariable || lookup[associatedVariable]) {
                    return;
                }

                lookup[associatedVariable] = field;
            });
        });

    return lookup;
};

/** Reads declared location schema from raw CMS data; undefined means it could not be verified. */
export const readOrganizationOwnedLocationSchema = (
    response: any,
    organizationId?: string,
    sharedConfiguration: boolean = false
): ApplicationModuleFieldLookup | undefined => {
    const modules = getRawModules(response).filter(module => !organizationId || getTemplateConfigurationOrganizationId(module) === organizationId);
    if (!modules.length) return undefined;
    const lookup: ApplicationModuleFieldLookup = {};
    for (const module of modules) {
        const form = readValue<any>(module, "templateForm", "TemplateForm");
        const sections = readValue<any[]>(form, "templateFormSections", "TemplateFormSections");
        const directFields = readValue<any[]>(module, "templateFields", "TemplateFields")
            || readValue<any[]>(module, "fields", "Fields");
        let fields: ApplicationModuleField[];
        if (Array.isArray(sections)) {
            if (sections.some(section => !Array.isArray(readValue<any[]>(section, "templateFormFields", "TemplateFormFields")))) return undefined;
            fields = getNestedTemplateFormFields(module);
        } else if (Array.isArray(directFields)) {
            fields = directFields.map(normalizeModuleField);
        } else {
            return undefined;
        }
        fields.forEach(field => {
            const variable = field.associatedVariable?.trim();
            if (sharedConfiguration) {
                const savedFields = (readValue<any[]>(module, "templateFormFieldValues", "TemplateFormFieldValues") || []).map(normalizeModuleField);
                const saved = savedFields.find(value => (field.id && value.templateFormFieldId === field.id)
                    || (variable && value.associatedVariable === variable));
                const value = saved?.currentValue ?? saved?.value ?? field.currentValue ?? field.value;
                // Match Spark's shared-form visibility, including its schema-default exception.
                if (!isFieldVisible(field as any, value, true)) return;
            }
            if (variable) lookup[variable] = field;
        });
    }
    return lookup;
};

/** Reads the selected location fields exactly as Spark Configuration does. */
export const flattenOrganizationOwnedTemplateFormFields = (
    appliedTemplates: any[],
    organizationId: string
): ApplicationModuleFieldLookup => {
    const lookup: ApplicationModuleFieldLookup = {};
    (appliedTemplates || [])
        .filter(template => getTemplateConfigurationOrganizationId(template) === organizationId)
        .forEach(template => getNestedTemplateFormFields(template).forEach(field => {
            if (field.associatedVariable) {
                lookup[field.associatedVariable] = field;
            }
        }));
    return lookup;
};

export const flattenApplicationModuleFieldsList = (response?: ApplicationModuleFieldsResponse | any): FlattenedApplicationModuleField[] => {
    return extractFlattenedApplicationModuleFields(response);
};

export const getApplicationModuleFieldError = getResultError;
